import { Text, TextInput, Skeleton, ScrollArea, Button, Loader } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useState, useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import type { TestContentWithSimpleSteps } from '~/server/api/routers/test';
import { CopyStepTestView } from './CopyStepTestView';
import { CopyStepTestInfo } from './CopyStepTestInfo';
import { MatchingStepsWithEllipses } from '../../MatchingStepsWithEllipses';
import clsx from 'clsx';

const LIMIT = 10;

// Skeleton component for loading states
function SearchResultsSkeleton() {
  return (
    <ScrollArea.Autosize className="mt-4 h-full">
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          className="mx-2 mb-2 rounded-md border border-gray-100 px-2 py-3 dark:border-gray-700"
        >
          <div className="mb-2">
            <Skeleton
              height={30}
              width="60%"
              className="mb-1"
            />
            <Skeleton
              height={30}
              width="40%"
            />
          </div>
          <div className="mt-2">
            <Skeleton
              height={30}
              width="80%"
              className="mb-1"
            />
            <Skeleton
              height={30}
              width="70%"
            />
          </div>
        </div>
      ))}
    </ScrollArea.Autosize>
  );
}

function TestContentItem({
  testContent,
  copyIntoTestContentId,
  insertAfterStepOrder,
  debouncedSearchTerm,
}: {
  testContent: TestContentWithSimpleSteps & { createdByUserEmail: string; updatedByUserEmail: string };
  copyIntoTestContentId: string;
  insertAfterStepOrder: number;
  debouncedSearchTerm: string;
}) {
  return (
    <div
      onClick={() => {
        if (testContent.steps.length === 0) {
          return;
        }
        modals.open({
          modalId: 'copy-step-test-view',
          title: <div className="text-lg font-bold">Copy steps from existing test</div>,
          centered: true,
          children: (
            <CopyStepTestView
              copyIntoTestContentId={copyIntoTestContentId}
              insertAfterStepOrder={insertAfterStepOrder}
              copyFromTestContent={testContent}
            />
          ),
          size: '80%',
        });
      }}
      className={clsx(
        'mx-2 mb-2 rounded-md border border-gray-100 px-2 py-3 dark:border-gray-700',
        testContent.steps.length > 0 && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800',
        testContent.steps.length === 0 && 'cursor-not-allowed opacity-50',
      )}
    >
      <CopyStepTestInfo
        testContent={testContent}
        copyIntoTestContentId={copyIntoTestContentId}
        updatedByUserEmail={testContent.updatedByUserEmail}
      />
      <div className="mt-1">
        <MatchingStepsWithEllipses
          simpleSteps={testContent.steps}
          searchTerm={debouncedSearchTerm}
        />
      </div>
    </div>
  );
}

// Modal component that handles searching for tests and copying steps.
function CopyStepsModal({
  copyIntoTestContentId,
  insertAfterStepOrder,
}: {
  copyIntoTestContentId: string;
  insertAfterStepOrder: number;
}) {
  const projectId = useProjectId();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm.trim(), 500);

  const {
    data: infiniteData,
    isLoading: isTestsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = api.test.getAllTestsWithSimpleStepsInfinite.useInfiniteQuery(
    {
      projectId: projectId ?? '',
      limit: LIMIT,
      search: debouncedSearchTerm,
    },
    {
      enabled: !!projectId,
      getNextPageParam: (lastPage: { items: TestContentWithSimpleSteps[]; nextCursor?: string }) => lastPage.nextCursor,
    },
  );

  // Flatten the infinite query data
  const testContentsData = useMemo(() => {
    return infiniteData?.pages.flatMap((page) => page.items) ?? [];
  }, [infiniteData]);

  // Get user IDs for email lookup
  const userIds = useMemo(
    () => [
      ...new Set([
        ...(testContentsData.map((testContent) => testContent?.createdByUserId).filter((userId) => userId !== null) ??
          []),
        ...(testContentsData.map((testContent) => testContent?.updatedByUserId).filter((userId) => userId !== null) ??
          []),
      ]),
    ],
    [testContentsData],
  );

  const { data: users } = api.team.getUsersInfo.useQuery({ userIds }, { enabled: userIds.length > 0 });

  // Enhance testContents with user email information
  const testContentsWithEmails = useMemo(() => {
    return testContentsData.map((item) => ({
      ...item,
      createdByUserEmail: users?.[item.createdByUserId ?? '']?.email ?? '',
      updatedByUserEmail: users?.[item.updatedByUserId ?? '']?.email ?? '',
    }));
  }, [testContentsData, users]);

  return (
    <div className="flex h-[80vh] flex-col overflow-hidden">
      <div className="flex-shrink-0">
        <TextInput
          placeholder="Enter test name or step description"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          data-autofocus
        />
      </div>

      {isTestsLoading ?
        <SearchResultsSkeleton />
      : testContentsWithEmails.length > 0 ?
        <ScrollArea.Autosize
          className="mt-4 h-full"
          onBottomReached={() => {
            if (hasNextPage && !isFetchingNextPage) {
              void fetchNextPage();
            }
          }}
        >
          {testContentsWithEmails?.map((testContent) => (
            <TestContentItem
              key={testContent.id}
              testContent={testContent}
              copyIntoTestContentId={copyIntoTestContentId}
              insertAfterStepOrder={insertAfterStepOrder}
              debouncedSearchTerm={debouncedSearchTerm}
            />
          ))}

          {/* Load more indicator and button */}
          <div className="mx-2 mb-4 flex justify-center">
            {isFetchingNextPage ?
              <div className="flex h-8 items-center gap-2 text-gray-600 dark:text-gray-400">
                <Loader size="xs" />
                <Text size="sm">Loading more tests...</Text>
              </div>
            : hasNextPage ?
              <Button
                variant="subtle"
                size="sm"
                onClick={() => void fetchNextPage()}
                className="h-8"
              >
                Load More Tests
              </Button>
            : <Text
                size="sm"
                c="dimmed"
                fs="italic"
              >
                That's all the tests!
              </Text>
            }
          </div>
        </ScrollArea.Autosize>
      : <div className="mt-4 h-full">
          <Text
            size="sm"
            c="dimmed"
          >
            No tests found
          </Text>
        </div>
      }
    </div>
  );
}

// React hook that returns a function to open the copy steps dialog for a given stepId.
export function useCopyStepsDialog() {
  const openCopyStepsDialog = ({
    insertAfterStepOrder,
    copyIntoTestContentId,
  }: {
    insertAfterStepOrder: number;
    copyIntoTestContentId: string;
  }) => {
    modals.open({
      title: <div className="text-lg font-bold">Copy steps from existing test</div>,
      centered: true,
      children: (
        <CopyStepsModal
          insertAfterStepOrder={insertAfterStepOrder}
          copyIntoTestContentId={copyIntoTestContentId}
        />
      ),
      size: '80%',
    });
  };

  return { openCopyStepsDialog };
}
